import * as React from 'react';
import { CONTRACT_LAYER_TYPES, contractLayerFields, OIL_AND_GAS_LAYER_FIELDS } from './ContractLayers';

/*
 * ControlSummary
 * --------------
 * At-a-glance answer to the question clients actually ask on the map:
 * "Do we control this tract, and if not, who is ahead of us?"
 *
 * This reads the same server-computed WI composition fields that the map's leasehold
 * hatch is drawn from (baked into each tract's GeoJSON properties by GISService):
 *
 *   WI_Client_NetAcres            net acres the client controls (WI on active leases)
 *   WI_Total_Controlled_NetAcres  total WI-controlled net acres (the LEASED portion)
 *   WI_Other_Owner / _NetAcres    the single largest non-client WI owner
 *   WI_Other_Count                number of distinct non-client WI owners
 *   WI_Unleased_NetAcres          net acres of ownerships with no active lease
 *   WI_Gross_Acres                the tract's gross acres  <- the % denominator
 *   WI_Leased_NetAcres            mineral acres under an active lease (not WI-weighted)
 *   WI_Open_NetAcres              gross less leased = open acreage
 *
 * That is oil and gas, the Leasehold layer. Given a contract layer's prefix it reads that lease
 * type's figures instead, the same names under the prefix (Brine_Client_NetAcres, ...), which the
 * tracts file carries; the side panel's Leaseholds Brine tab passes its layer's prefix.
 *
 * THE DENOMINATOR IS THE WHOLE TRACT, OPEN ACREAGE INCLUDED. "50% of the leased acreage"
 * reads as control the client doesn't have: a 100-ac tract with 25 ac client, 25 ac
 * competitor and 50 ac open is 25% controlled, not 50%. Control (the headline) is
 * therefore client net acres > half of the ENTIRE tract. Net acres come from mineral
 * interest x tract acreage, NOT the frequently-null ProvisionalNetAcres.
 *
 * Older baked files predate WI_Gross_Acres / WI_Open_NetAcres, so the denominator falls
 * back to Legal_Acre (baked since the beginning) and then to leased + unleased net acres.
 *
 * COVERAGE vs. NET ACRES — a tract can be leased (active leasehold, a WI owner painted
 * on the map) yet still have WI_Total_Controlled_NetAcres = 0 when the underlying
 * mineral interest / gross acreage needed to compute net acres isn't recorded. The map
 * paints it anyway (GISService falls back to WI_Owner when the net-acre query is empty),
 * so this summary must not read a zero denominator as "open". We therefore key the
 * open/leased decision off the same coverage fields the map uses (WI_Owner /
 * WI_Composition / Is_Client_Owned) and only show a % when net acres are actually present.
 */

interface IControlSummaryProps {
    attributes: Record<string, string | number> | undefined;
    /** Present only after a tract has been clicked. */
    hasTract: boolean;
    /** A contract layer's prefix ('Brine') to summarize that lease type instead of oil and gas. */
    contractPrefix?: string;
}

/* Where one lease type's figures are, and how the summary words it. */
interface ILeaseTypeFields {
    /** What the acreage figures are named under: WI_Client_NetAcres, Brine_Client_NetAcres, ... */
    acresPrefix: string;
    owner: string;
    composition: string;
    clientOwned: string;
    noCoverage: string;
    coveredHeadline: string;
    heldBy: (owners: string) => string;
    otherLabel: string;
}

const OIL_AND_GAS: ILeaseTypeFields = {
    acresPrefix: 'WI',
    ...OIL_AND_GAS_LAYER_FIELDS,
    noCoverage: 'No leasehold coverage',
    coveredHeadline: 'Tract is leased',
    heldBy: (owners) => `Working interest: ${owners}.`,
    otherLabel: 'Largest other WI owner',
};

const leaseTypeFields = (contractPrefix: string | undefined): ILeaseTypeFields => {
    if (!contractPrefix) {
        return OIL_AND_GAS;
    }
    // 'Brine Lease' -> 'brine lease'
    const kind = (CONTRACT_LAYER_TYPES.find((t) => t.prefix === contractPrefix)?.agreementType ?? contractPrefix).toLowerCase();
    return {
        acresPrefix: contractPrefix,
        ...contractLayerFields(contractPrefix),
        noCoverage: `No ${kind} coverage`,
        coveredHeadline: `Tract is under ${kind}`,
        heldBy: (owners) => `Held by ${owners}.`,
        otherLabel: contractPrefix === 'Brine' ? 'Largest other brine lessee' : 'Largest other holder',
    };
};

/* Parse a possibly-absent numeric attribute; undefined means "field not present". */
const num = (v: unknown): number | undefined => {
    if (v === null || v === undefined || v === '') {
        return undefined;
    }
    const n = Number(v);
    return isNaN(n) ? undefined : n;
};

/* First value that's actually an acreage. Both WI_Gross_Acres and Legal_Acre are baked as
 * 0 when a tract's gross acres aren't recorded, so 0 has to read as "unknown", not as a
 * total to divide by. */
const firstPositive = (...vals: Array<number | undefined>): number | undefined => vals.find((v) => v !== undefined && v > 0);

const fmtAcres = (n: number): string => n.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

/* Whole percents, but a share under 9% to a tenth (3.7%), where a whole percent would hide most of
 * it; a sliver under 0.05% reads <0.1% rather than 0.0%. */
export const fmtPct = (fraction: number): string => {
    const pct = fraction * 100;
    if (pct > 0 && pct < 9) {
        return pct < 0.05 ? '<0.1%' : `${(Math.round(pct * 10) / 10).toFixed(1)}%`;
    }
    return `${Math.round(pct)}%`;
};

const ControlSummary: React.FC<IControlSummaryProps> = ({ attributes, hasTract, contractPrefix }) => {
    if (!hasTract) {
        return null;
    }

    const type = leaseTypeFields(contractPrefix);
    const acres = (name: string): number | undefined => num(attributes?.[`${type.acresPrefix}_${name}`]);

    /* Leased (WI-controlled) net acres. Present since the first WI bake, so its absence
     * marks a pre-WI GeoJSON file rather than an open tract. */
    const totalNet = acres('Total_Controlled_NetAcres');
    const clientNet = acres('Client_NetAcres') ?? 0;
    const unleased = acres('Unleased_NetAcres') ?? 0;
    const otherName = (attributes?.[`${type.acresPrefix}_Other_Owner`] as string | undefined) || undefined;
    const otherNet = acres('Other_NetAcres');
    const otherCount = acres('Other_Count') ?? 0;

    /* --- Whole-tract basis. Gross acres from the WI bake, else the long-standing
     * Legal_Acre, else (older files) leased + unleased as the best available stand-in. --- */
    const grossAcres = firstPositive(num(attributes?.WI_Gross_Acres), num(attributes?.Legal_Acre));
    const leasedMineral = acres('Leased_NetAcres');
    const hasGross = grossAcres !== undefined;
    const tractAcres = hasGross ? (grossAcres as number) : totalNet !== undefined && totalNet + unleased > 0 ? totalNet + unleased : undefined;

    /* Open acreage: whatever active leases don't account for. Prefer the baked figure;
     * otherwise derive it, never reporting less than the unleased-ownership acreage. */
    const openAcres = Math.max(
        0,
        acres('Open_NetAcres') ?? (tractAcres !== undefined ? Math.max(unleased, tractAcres - (leasedMineral ?? totalNet ?? 0)) : unleased)
    );

    /* Spelled out because "% controlled" is only meaningful against a stated denominator. */
    const basisNote = hasGross
        ? `Percentages are of the tract's ${fmtAcres(tractAcres as number)} gross acres, open acreage included.`
        : `Gross acres not recorded for this tract — percentages are of ${fmtAcres((tractAcres as number) || 0)} leased + unleased net acres.`;

    /* Leasehold coverage signal — the same fields the map paints the tract from. A tract
     * is "covered" when it has a real WI owner (not just unleased) or is client-owned,
     * even if net acres computed to zero. See the COVERAGE vs. NET ACRES note above. */
    const wiOwner = (attributes?.[type.owner] as string | undefined)?.trim() || undefined;
    const composition = (attributes?.[type.composition] as string | undefined) || undefined;
    const isClientOwned = num(attributes?.[type.clientOwned]) === 1;
    const compositionTokens = composition
        ? composition
              .split('|')
              .map((t) => t.trim())
              .filter((t) => t.length > 0)
        : [];
    const hasLeaseholdCoverage = isClientOwned || (!!wiOwner && wiOwner !== 'Unleased') || compositionTokens.some((t) => t !== '__UNLEASED__');

    const coveredOwnerLabel =
        wiOwner && wiOwner !== 'Unleased'
            ? wiOwner
                  .split('|')
                  .map((n) => n.trim())
                  .filter((n) => n.length > 0)
                  .join(', ')
            : undefined;

    /* Covered by a leasehold, but net acres are missing/zero so no % can be computed.
     * Report the coverage honestly rather than calling the tract open. */
    const coveredSummary = (
        <div className={`tract-control-summary ${isClientOwned ? 'controls' : 'behind'}`}>
            <div className="control-summary-primary">
                <span className="control-summary-headline">{isClientOwned ? 'Client controls this tract' : type.coveredHeadline}</span>
                <span className="control-summary-detail">
                    {coveredOwnerLabel
                        ? `${type.heldBy(coveredOwnerLabel)} Control % unavailable — net acreage not recorded.`
                        : 'Control % unavailable — net acreage not recorded.'}
                </span>
            </div>
        </div>
    );

    /* Net-acre field absent (older files never computed it). If the tract is nonetheless
     * covered per the map fields, show that; otherwise we truly can't say anything. */
    if (totalNet === undefined) {
        return hasLeaseholdCoverage ? (
            coveredSummary
        ) : (
            <div className="tract-control-summary is-loading">
                <span className="control-summary-loading">Control data unavailable for this tract.</span>
            </div>
        );
    }

    /* No WI-controlled net acres. Only "open" if the tract also has no leasehold coverage;
     * a leased tract with zero computed net acres falls through to the covered summary. */
    if (totalNet <= 0) {
        return hasLeaseholdCoverage ? (
            coveredSummary
        ) : (
            <div className="tract-control-summary is-open">
                <span className="control-summary-headline">{type.noCoverage}</span>
                <span className="control-summary-detail">
                    This tract appears open{openAcres > 0 ? ` (${fmtAcres(openAcres)} open ac)` : ''} — 0% controlled.
                </span>
            </div>
        );
    }

    /* Nothing to divide by (no gross acres, no leased/unleased totals) — report the
     * coverage instead of inventing a percentage. */
    if (tractAcres === undefined) {
        return coveredSummary;
    }

    /* Control is a majority of the WHOLE tract, so open acreage counts against the client
     * exactly as a competitor's leasehold does. */
    const clientControls = clientNet > tractAcres / 2;
    const clientFraction = clientNet / tractAcres;
    const statusClass = clientControls ? 'controls' : 'behind';
    const additionalOthers = otherCount > 1 ? otherCount - 1 : 0;

    /* Rows for the narrow side panel: the headline with the client's share at its right and the
     * acres under it; then each detail row's label at the left and its acres and share at the right
     * on one line, the owner's name under them. */
    return (
        <div className={`tract-control-summary ${statusClass}`}>
            <div className="control-summary-primary">
                <span className="control-summary-headline">
                    {clientControls ? 'Client controls this tract' : 'Client does not control this tract'}
                </span>
                <span className="control-summary-metric">{fmtPct(clientFraction)}</span>
            </div>
            <span className="control-summary-metric-sub">{`${fmtAcres(clientNet)} of ${fmtAcres(tractAcres)} ac controlled`}</span>
            {otherName && (
                <div className="control-summary-row">
                    <span className="control-summary-label">{type.otherLabel}</span>
                    {otherNet !== undefined && (
                        <span className="control-summary-figure">{`${fmtAcres(otherNet)} net ac (${fmtPct(otherNet / tractAcres)})`}</span>
                    )}
                    <span className="control-summary-name">
                        {otherName}
                        {additionalOthers > 0 && ` + ${additionalOthers} other${additionalOthers === 1 ? '' : 's'}`}
                    </span>
                </div>
            )}
            {openAcres > 0 && (
                <div className="control-summary-row">
                    <span className="control-summary-label">Open</span>
                    <span className="control-summary-figure">{`${fmtAcres(openAcres)} ac (${fmtPct(openAcres / tractAcres)})`}</span>
                </div>
            )}
            <span className="control-summary-note">{basisNote}</span>
        </div>
    );
};

export default ControlSummary;



// WEBPACK FOOTER //
// ./src/components/project/sections/GISComponents/ControlSummary.tsx